/**
 * Distance calculation utilities using Haversine formula
 * for great-circle distance between two coordinates
 */

export interface Coordinates {
  lat: number;
  lng: number;
}

/**
 * Converts degrees to radians
 */
const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

/**
 * Calculates the great-circle distance between two points on Earth
 * using the Haversine formula
 *
 * @param coord1 - First coordinate (e.g., home location)
 * @param coord2 - Second coordinate (e.g., delivery address)
 * @returns Distance in kilometers
 */
export const calculateDistance = (coord1: Coordinates, coord2: Coordinates): number => {
  // Validate coordinates
  if (!isFinite(coord1.lat) || !isFinite(coord2.lat) || !isFinite(coord1.lng) || !isFinite(coord2.lng)) {
    console.error('[Distance] Invalid coordinates provided');
    return 0;
  }

  const EARTH_RADIUS_KM = 6371; // Earth's radius in kilometers

  const lat1Rad = toRadians(coord1.lat);
  const lat2Rad = toRadians(coord2.lat);
  const deltaLat = toRadians(coord2.lat - coord1.lat);
  const deltaLng = toRadians(coord2.lng - coord1.lng);

  // Haversine formula
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = EARTH_RADIUS_KM * c;

  return distance;
};

/**
 * Calculates distance from home to a delivery address
 *
 * @param homeCoords - Home/business coordinates
 * @param deliveryCoords - Delivery address coordinates
 * @param precision - Decimal places to round to (default: 2)
 * @returns Distance in kilometers, rounded to specified precision
 */
export const calculateDistanceFromHome = (
  homeCoords: Coordinates,
  deliveryCoords: Coordinates,
  precision: number = 2
): number => {
  const distance = calculateDistance(homeCoords, deliveryCoords);
  return Number(distance.toFixed(precision));
};

/**
 * Formats distance for display
 *
 * @param distance - Distance in kilometers
 * @param showUnit - Whether to append "km" (default: true)
 * @returns Formatted distance string
 */
export const formatDistance = (distance: number | null | undefined, showUnit: boolean = true): string => {
  if (distance === null || distance === undefined) {
    return 'N/A';
  }

  const formatted = distance.toFixed(2);
  return showUnit ? `${formatted} km` : formatted;
};
